import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useStore } from "../../../stores/helpers/useStore";
import { observer } from "mobx-react-lite";
import Footer from "../../../components/Footer/Footer";
import "./RegisterPage.css";

const initialValues = {
	email: "",
	password: "",
	confirmPassword: "",
};

const registrationSchema = Yup.object().shape({
	email: Yup.string()
		.trim()
		.required("Email is required")
		.email("Email must be valid"),
	password: Yup.string()
		.trim()
		.min(6, "Minimum 6 characters")
		.max(20, "Maximum 20 characters")
		.matches(
			/^(?=.*[a-zA-Z])(?=.*[0-9]).+$/,
			"Should have at least one number and one letter"
		)
		.required("Password is required"),
	confirmPassword: Yup.string()
		.trim()
		.required("Password confirmation is required")
		.oneOf([Yup.ref("password")], "Passwords must match"),
});

const RegisterPage = (props) => {
	const {
		authStore: { registerUser },
	} = useStore();
	const { history } = props;

	const formik = useFormik({
		initialValues,
		validationSchema: registrationSchema,
		onSubmit: async (values, { setFieldError, setSubmitting }) => {
			setSubmitting(true);
			try {
				await registerUser(values.email, values.password);
			} catch (err) {
				setSubmitting(false);
				if (err?.response?.status === 409) {
					setFieldError(
						err?.response?.data?.param,
						err?.response?.data?.message
					);
				} else if (err?.response?.status === 500) {
					history.push("/pages/errors/error-500");
				}
			}
		},
	});

	return (
		<>
			<div className="register-page">
				<div className="form-section">
					<div className="form-container">
						<div className="form-header">
							<h2>Create account</h2>
							<p>TriviaRat Studio</p>
						</div>

						<form onSubmit={formik.handleSubmit} noValidate>
							<div className="form-group">
								<label htmlFor="register-email">Email</label>
								<input
									id="register-email"
									type="email"
									name="email"
									placeholder="you@company.com"
									value={formik.values.email}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									className={formik.touched.email && formik.errors.email ? "error" : ""}
								/>
								{formik.touched.email && formik.errors.email && (
									<span className="error-text">{formik.errors.email}</span>
								)}
							</div>

							<div className="form-group">
								<label htmlFor="register-password">Password</label>
								<input
									id="register-password"
									type="password"
									name="password"
									placeholder="6+ characters, letters & numbers"
									value={formik.values.password}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									className={formik.touched.password && formik.errors.password ? "error" : ""}
								/>
								{formik.touched.password && formik.errors.password && (
									<span className="error-text">{formik.errors.password}</span>
								)}
							</div>

							<div className="form-group">
								<label htmlFor="register-confirm">Confirm password</label>
								<input
									id="register-confirm"
									type="password"
									name="confirmPassword"
									placeholder="Same as above"
									value={formik.values.confirmPassword}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									className={
										formik.touched.confirmPassword && formik.errors.confirmPassword ? "error" : ""
									}
								/>
								{formik.touched.confirmPassword && formik.errors.confirmPassword && (
									<span className="error-text">{formik.errors.confirmPassword}</span>
								)}
							</div>

							<button type="submit" className="submit-button" disabled={formik.isSubmitting}>
								{formik.isSubmitting ? (
									<>
										<span className="spinner" aria-hidden="true" />
										Creating…
									</>
								) : (
									"Create account"
								)}
							</button>

							<div className="form-footer">
								<span className="form-footer-text">Already have an account?</span>{" "}
								<button
									type="button"
									className="link-button"
									onClick={() => history.push("/pages/auth/login")}
								>
									Sign in
								</button>
							</div>
						</form>
					</div>
				</div>

				<div className="hero-section">
					<div className="hero-overlay" />
					<div className="hero-content">
						<div className="hero-badge">Studio</div>
						<h1 className="hero-title">Enterprise trivia, simplified.</h1>
						<p className="hero-tagline">
							Hosts, rounds, and branding — managed from one place.
						</p>
						<ul className="hero-highlights" aria-label="Studio highlights">
							<li>Multi-host dashboard</li>
							<li>Shared rounds &amp; settings</li>
							<li>Ready for classrooms &amp; venues</li>
						</ul>
					</div>
				</div>
			</div>
			<Footer />
		</>
	);
};

export default observer(RegisterPage);
